import { ts } from '@shared/lib/diagTs';
import { createContext, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import * as signalR from '@microsoft/signalr';
import { buildHubConnection } from './hubClient';
import { REALTIME_EVENT_NAMES, type RealtimeEventMap, type RealtimeEventName } from './events';
import { useAccessTokenProvider } from '@core/auth/useAccessToken';
import { useAuth } from 'react-oidc-context';
import { devAuthUser } from '@config/env';

type Listener<E extends RealtimeEventName> = (payload: RealtimeEventMap[E]) => void;

interface RealtimeContextValue {
  state: signalR.HubConnectionState;
  subscribe<E extends RealtimeEventName>(eventName: E, listener: Listener<E>): () => void;
}

const RealtimeContext = createContext<RealtimeContextValue | null>(null);

export function RealtimeProvider({ children }: { children: ReactNode }) {
  // In dev-auth mode, AuthProvider isn't mounted — useAuth would throw. Skip it.
  // The conditional hook call here mirrors a module-level constant, so call order is stable per render.
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const isAuthenticated = devAuthUser ? true : useAuth().isAuthenticated;
  const getAccessToken = useAccessTokenProvider();
  const connectionRef = useRef<signalR.HubConnection | null>(null);
  const [state, setState] = useState<signalR.HubConnectionState>(signalR.HubConnectionState.Disconnected);

  // IMPORTANT: useAccessTokenProvider() returns a fresh function on every render. Putting it in
  // the effect deps tore down + recreated the SignalR connection every render — the new
  // connection's negotiate request got aborted by the cleanup of the previous render's
  // connection, producing "stopped during negotiation" in a tight loop. Stash the latest
  // resolver in a ref and depend only on auth state.
  const getAccessTokenRef = useRef(getAccessToken);
  getAccessTokenRef.current = getAccessToken;

  // Event fan-out map: SignalR client methods are registered for EVERY known event at connection
  // start (below); components subscribe/unsubscribe against this map. Registering `.on` lazily per
  // subscriber meant a push arriving while no consumer was mounted was DROPPED by @microsoft/signalr
  // ("No client method with the name 'X' found") — e.g. `positions.cqg` while the dashboard was on
  // the Closed tab, `account.summary` off the PnL page, `cqg.import.complete` after the dialog closed.
  const listenersRef = useRef(new Map<RealtimeEventName, Set<(payload: unknown) => void>>());

  useEffect(() => {
    if (!isAuthenticated) return;

    const connection = buildHubConnection({
      getAccessToken: () => getAccessTokenRef.current(),
    });
    connectionRef.current = connection;

    // Register a client method for ALL known server events up-front — pushes are never dropped for
    // want of a handler; they fan out to whoever is subscribed at delivery time (possibly nobody).
    for (const eventName of REALTIME_EVENT_NAMES) {
      connection.on(eventName, (payload: unknown) => {
        const set = listenersRef.current.get(eventName);
        if (!set || set.size === 0) return;
        for (const listener of [...set]) {
          try { listener(payload); } catch { /* one bad listener must not break the rest */ }
        }
      });
    }

    const sync = () => setState(connection.state);
    connection.onreconnecting(sync);
    connection.onreconnected(sync);
    connection.onclose(sync);

    let cancelled = false;
    connection
      .start()
      .then(() => { if (!cancelled) sync(); })
      .catch((err) => {
        if (cancelled) return;  // expected when StrictMode tears the effect down mid-start
        console.error(ts(), '[realtime] connect failed', err);
        sync();
      });

    return () => {
      cancelled = true;
      connection.stop().catch(() => {});
      connectionRef.current = null;
    };
  }, [isAuthenticated]);

  const value = useMemo<RealtimeContextValue>(
    () => ({
      state,
      // Adds/removes a listener in the fan-out map — the SignalR client method itself is already
      // registered (for every event) at connection start, so subscribing works even before the
      // connection exists and no push is ever dropped for want of a handler.
      subscribe<E extends RealtimeEventName>(eventName: E, listener: Listener<E>) {
        let set = listenersRef.current.get(eventName);
        if (!set) { set = new Set(); listenersRef.current.set(eventName, set); }
        const wrapped = (payload: unknown) => listener(payload as RealtimeEventMap[E]);
        set.add(wrapped);
        return () => { set.delete(wrapped); };
      },
    }),
    [state],
  );

  return <RealtimeContext.Provider value={value}>{children}</RealtimeContext.Provider>;
}

export function useRealtime() {
  const ctx = useContext(RealtimeContext);
  if (!ctx) throw new Error('useRealtime must be used inside <RealtimeProvider>');
  return ctx;
}

export function useRealtimeEvent<E extends RealtimeEventName>(
  eventName: E,
  listener: Listener<E>,
): void {
  const realtime = useRealtime();
  useEffect(() => realtime.subscribe(eventName, listener), [realtime, eventName, listener]);
}
